<script lang="ts">
import type { AcceptableValue } from '@nuxt/ui';
import type { SelectMenuEmits, SelectMenuProps } from './SelectMenu.vue';

export interface OptionSelectItem {
	value: AcceptableValue;
	label?: string;
	icon?: string;
	color?: string;
}

export interface OptionSelectProps {
	modelValue?: AcceptableValue | AcceptableValue[];
	items: OptionSelectItem[];
	multiple?: boolean;
	disabled?: boolean;
	readonly?: boolean;
	clear?: boolean;
	createItem?: SelectMenuProps['createItem'];
	open?: boolean;
	placeholder?: string;
	removeIcon?: string;
	trailingIcon?: string;
	icon?: string;
	size?: SelectMenuProps['size'];
	variant?: SelectMenuProps['variant'];
	ui?: SelectMenuProps['ui'];
	modelModifiers?: SelectMenuProps['modelModifiers'];
}

export interface OptionSelectEmits extends SelectMenuEmits<OptionSelectItem[], 'value', boolean> {
	removeItem: [value: AcceptableValue];
}
</script>

<script setup lang="ts">
import { createReusableTemplate, reactiveOmit } from '@vueuse/core';
import { omit } from 'lodash-es';
import { useEmitAsProps, useForwardProps } from 'reka-ui';
import { computed } from 'vue';
import { mergeUi } from '#layers/ui/utils/merge-ui';

defineOptions({ inheritAttrs: false });

const props = defineProps<OptionSelectProps>();
const emit = defineEmits<OptionSelectEmits>();
const forwardedProps = useForwardProps(reactiveOmit(props, 'removeIcon'));
// `removeItem` belongs to this wrapper; every inherited DSelectMenu event forwards automatically.
const forwardedEmits = omit(useEmitAsProps(emit), ['onRemoveItem']);

const hasValue = computed(() => {
	if (props.multiple) return Array.isArray(props.modelValue) && props.modelValue.length > 0;
	return props.modelValue !== undefined && props.modelValue !== null && props.modelValue !== '';
});

const selectedOptions = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : [props.modelValue])
	.map((value) => props.items.find((option) => option.value === value))
	.filter((option): option is OptionSelectItem => option !== undefined));

function getDisplayValue(option: OptionSelectItem) {
	return option.label ?? option.value;
}

const [DefineItemIcon, ReuseItemIcon] = createReusableTemplate<{ item: OptionSelectItem; iconClass?: string }>({ inheritAttrs: false });
const [DefineValueItem, ReuseValueItem] = createReusableTemplate<{ item: OptionSelectItem; iconClass?: string }>({ inheritAttrs: false });
</script>

<template>
	<DefineItemIcon v-slot="{ item, iconClass }">
		<span v-if="item.icon || item.color" class="inline-flex items-center justify-center" :class="iconClass">
			<DIcon v-if="item.icon" :name="item.icon" :style="{ color: item.color }" />
			<DColorDot v-else-if="item.color" :color="item.color" size="xs" />
		</span>
	</DefineItemIcon>

	<DefineValueItem v-slot="{ item, iconClass }">
		<span class="pointer-events-none inline-flex min-w-0 items-center gap-1.5">
			<ReuseItemIcon :item :icon-class />
			<span class="min-w-0 truncate">{{ getDisplayValue(item) }}</span>
		</span>
	</DefineValueItem>

	<DSelectMenu
		v-bind="{ ...forwardedProps, ...forwardedEmits, ...$attrs }"
		value-key="value"
		:search-input="items.length > 5"
		:aria-readonly="readonly"
		:ui="mergeUi(ui, {
			base: 'pointer-events-auto w-full flex-wrap gap-1',
		})"
	>
		<template #default="{ modelValue: selectedValue }">
			<slot v-if="hasValue" v-bind="{ modelValue, multiple, options: selectedOptions }">
				<template v-if="multiple && Array.isArray(modelValue)">
					<DNotFieldGroup>
						<DBadge
							v-for="option in selectedOptions"
							:key="option.value as PropertyKey"
							variant="outline"
							color="neutral"
						>
							<ReuseValueItem :item="option" />
							<template #trailing>
								<DButton
									variant="link"
									color="neutral"
									size="xs"
									class="-m-1 -mr-2"
									tabindex="-1"
									:icon="!readonly ? removeIcon : undefined"
									@click.stop.prevent="emit('removeItem', option.value)"
								/>
							</template>
						</DBadge>
					</DNotFieldGroup>
				</template>
				<template v-else-if="selectedOptions.length > 0">
					<ReuseValueItem v-for="option in selectedOptions" :key="option.value as PropertyKey" :item="option" />
				</template>
				<span v-else>{{ selectedValue }}</span>
			</slot>
			<span v-else data-slot="placeholder" class="truncate text-dimmed">{{ placeholder }}</span>
		</template>

		<template v-if="!readonly" #item-leading="{ item, ui: itemUi }">
			<ReuseItemIcon :item :icon-class="itemUi?.itemLeadingIcon()" />
		</template>
	</DSelectMenu>
</template>
